/**
 * Adapter layer: maps backend wire shapes (VoiceEvent rows, RecognizeResult
 * responses) onto the local chat `Message` union consumed by the bubbles.
 *
 * The server stores VoiceEvent rows with a `role` ('user' | 'assistant') and a
 * `transcript`. Assistant rows additionally carry agent output once the agent
 * job completes: a structured `@@RESULT` summary surfaced as `actions[]`
 * (hh-4fc), `clarifyOptions[]` (hh-5nh) and an `undoChangeId` (hh-2dl).
 *
 * Discriminator for an assistant row → bubble kind:
 *   - clarifyOptions present → AIClarify
 *   - actions present OR undoChangeId present → AISummary
 *   - otherwise → AIText (plain reply / error)
 */
import { humanizeError } from '../../api';
import type { MessageRow } from '../../api/endpoints/messages.js';
import type {
  AgentActionWire,
  ClarifyOptionWire,
  RecognizeResultResponse,
} from '../../api/endpoints/voice.js';
import type {
  AIClarifyMessage,
  AIClarifyOption,
  AISummaryAction,
  AISummaryMessage,
  AITextMessage,
  Message,
  UserMessage,
} from './Message.js';

export { pollVoiceResult as pollUntilTerminal } from '../../api/endpoints/voice.js';
export type { RecognizeResultResponse } from '../../api/endpoints/voice.js';

/** Formats an ISO timestamp into the short HH:MM label the bubbles render. */
function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const ACTION_KINDS: ReadonlySet<AISummaryAction['kind']> = new Set([
  'create',
  'move',
  'delete',
]);

/** Maps wire actions onto AISummaryAction, dropping entries with an unknown kind. */
function toSummaryActions(wire: readonly AgentActionWire[] | undefined): AISummaryAction[] {
  if (wire === undefined) return [];
  const out: AISummaryAction[] = [];
  for (const a of wire) {
    if (!ACTION_KINDS.has(a.kind as AISummaryAction['kind'])) continue;
    out.push({
      kind: a.kind as AISummaryAction['kind'],
      name: a.name,
      ...(a.to !== null ? { to: a.to } : {}),
      ...(a.from !== null ? { from: a.from } : {}),
    });
  }
  return out;
}

/** Maps wire clarify options onto AIClarifyOption. */
function toClarifyOptions(wire: readonly ClarifyOptionWire[] | undefined): AIClarifyOption[] {
  if (wire === undefined) return [];
  return wire.map((o) => ({
    id: o.id,
    label: o.label,
    ...(o.hint !== null ? { hint: o.hint } : {}),
  }));
}

/** Maps a single history row from GET /messages onto a local Message. */
export function rowToMessage(row: MessageRow): Message {
  const time = formatTime(row.createdAt);
  if (row.role === 'user') {
    return { id: row.id, kind: 'user', text: row.content, time };
  }

  const options = toClarifyOptions(row.clarifyOptions);
  if (options.length > 0) {
    return {
      id: row.id,
      kind: 'ai_clarify',
      text: row.content,
      options,
      time,
    } satisfies AIClarifyMessage;
  }

  const actions = toSummaryActions(row.actions);
  const undoId = row.undoChangeId ?? null;
  if (actions.length > 0 || undoId !== null) {
    return {
      id: row.id,
      kind: 'ai_summary',
      text: row.content,
      actions,
      undoable: undoId !== null,
      ...(undoId !== null ? { agentActionId: undoId } : {}),
      time,
    } satisfies AISummaryMessage;
  }

  return { id: row.id, kind: 'ai_text', text: row.content, time };
}

/** Maps the full history payload, preserving server order (oldest → newest). */
export function rowsToFeed(rows: readonly MessageRow[]): readonly Message[] {
  return rows.map(rowToMessage);
}

/** Builds an optimistic user bubble appended before the server round-trip. */
export function makeOptimisticUser(id: string, text: string): UserMessage {
  return {
    id,
    kind: 'user',
    text,
    time: formatTime(new Date().toISOString()),
  };
}

/**
 * Maps a terminal RecognizeResult onto the assistant bubble.
 *  - failed                 → AIText (error)
 *  - clarifyOptions present  → AIClarify (options from @@RESULT)
 *  - summary present         → AISummary (actions + undo from @@RESULT)
 *  - otherwise               → AIText (plain reply / error fallback)
 *
 * Clarify is keyed off non-empty `clarifyOptions`, not the `needsClarification`
 * flag: that flag is derived agent-side from a literal "CLARIFY:" text prefix,
 * which the agent often omits when it leads with a summary paragraph — leaving
 * a clarification rendered as a button-less AISummary (hh-r5q, case-04). The
 * options array is the real signal, and this matches `rowToMessage`.
 */
export function resultToMessage(id: string, result: RecognizeResultResponse): Message {
  const time = formatTime(new Date().toISOString());

  if (result.status === 'failed') {
    const text = humanizeError(result.errorMessage, 'agent');
    return { id, kind: 'ai_text', text, time } satisfies AITextMessage;
  }

  const clarifyOptions = toClarifyOptions(result.clarifyOptions);
  if (result.needsClarification || clarifyOptions.length > 0) {
    return {
      id,
      kind: 'ai_clarify',
      text: result.summary ?? 'Уточни, пожалуйста',
      options: clarifyOptions,
      time,
    } satisfies AIClarifyMessage;
  }

  const summary = result.summary;
  if (summary !== null && summary.trim().length > 0) {
    const undoId = result.undoChangeId ?? null;
    return {
      id,
      kind: 'ai_summary',
      text: summary,
      actions: toSummaryActions(result.actions),
      undoable: undoId !== null,
      ...(undoId !== null ? { agentActionId: undoId } : {}),
      time,
    } satisfies AISummaryMessage;
  }

  return {
    id,
    kind: 'ai_text',
    text: humanizeError(result.errorMessage, 'agent'),
    time,
  } satisfies AITextMessage;
}
